/**
 * The cadence picker's side of cron (docs/JOB-LIFECYCLE.md 2.7).
 *
 * "Every weekday at 02:00" is the sentence people actually have; a cron string
 * is the escape hatch under it. So the picker writes the three shapes below and
 * hands the hub the same five fields it would have accepted typed, which keeps
 * one dialect in the system rather than a picker format and a cron format.
 *
 * This does not parse cron and does not compute a next run. Both live in
 * `@dyspatch/core`, and both are *values*, so importing them here would pull
 * `node:crypto` into the browser bundle; the hub sends `cadence` and `nextRunAt`
 * already computed for exactly that reason.
 */

export type CadenceKind = 'weekdays' | 'daily' | 'weekly' | 'cron';

export interface Cadence {
  kind: CadenceKind;
  /** `HH:MM`, as the time input writes it. Ignored when `kind` is `cron`. */
  time: string;
  /** 0 is Sunday, for the weekly shape. */
  weekday: number;
  /** The raw expression, for `cron` and as what the others compile to. */
  expression: string;
}

export const WEEKDAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export const CADENCE_OPTIONS: readonly { value: CadenceKind; label: string }[] = [
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'cron', label: 'Cron' },
];

export const DEFAULT_CADENCE: Cadence = {
  kind: 'weekdays',
  time: '02:00',
  weekday: 1,
  expression: '0 2 * * 1-5',
};

function clockFields(time: string): { hour: number; minute: number } {
  const [hour, minute] = time.split(':');
  const h = Number(hour);
  const m = Number(minute);
  return {
    hour: Number.isFinite(h) ? Math.min(23, Math.max(0, Math.trunc(h))) : 2,
    minute: Number.isFinite(m) ? Math.min(59, Math.max(0, Math.trunc(m))) : 0,
  };
}

/** The five fields the picker's current state means. */
export function cronOf(cadence: Cadence): string {
  if (cadence.kind === 'cron') return cadence.expression.trim();
  const { hour, minute } = clockFields(cadence.time);
  const days =
    cadence.kind === 'weekdays' ? '1-5' : cadence.kind === 'weekly' ? String(cadence.weekday) : '*';
  return `${minute} ${hour} * * ${days}`;
}

/**
 * What the picker will produce, in the words it produced it in.
 *
 * A local echo of the hub's `describeCron` for the one case the form is in the
 * middle of: nothing has been saved, so there is no `cadence` from the hub to
 * print. Once the schedule exists the hub's sentence is what every surface
 * shows, so the two cannot drift where it matters.
 */
export function describeCadence(cadence: Cadence, timezone: string): string {
  if (cadence.kind === 'cron') return `${cadence.expression.trim()} (${timezone})`;
  const { hour, minute } = clockFields(cadence.time);
  const at = `at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${timezone}`;
  if (cadence.kind === 'weekdays') return `every weekday ${at}`;
  if (cadence.kind === 'daily') return `every day ${at}`;
  return `every ${WEEKDAY_LABELS[cadence.weekday] ?? 'Monday'} ${at}`;
}

/** Why the hub would refuse this expression, before it is asked. */
export function cadenceProblem(cadence: Cadence): string | null {
  const expression = cronOf(cadence);
  if (!expression) return 'A cadence needs an expression.';
  const fields = expression.split(/\s+/);
  if (fields.length !== 5) {
    return `A cron expression has five fields (minute hour day-of-month month day-of-week); this has ${fields.length}.`;
  }
  return null;
}
